/**
 * Admin write surface — thin wrapper around either IDB direct writes (static
 * mode) or the Fastify backend `/api/admin/*` (API mode). Admin components
 * import putCard / putPack / deleteCard / deletePack from THIS module instead
 * of `../db`, so flipping VITE_USE_API switches the whole write pipeline
 * without touching the component code.
 *
 * In API mode, after each successful server write we also write to the local
 * IDB so the admin UI shows the change instantly (without waiting for the
 * next ensureContent re-fetch). The IDB write here mirrors what the seed
 * loader does — it's a cache, the server is source of truth.
 */
import * as idb from "../db";
import type { Card, Pack } from "../domain/types";
import { isApiMode } from "../lib/apiMode";

/** Common fetch wrapper. Carries cookies (for JWT auth) and bubbles errors
 *  with a parsed body so AdminLogin etc. can show useful messages. */
async function apiFetch<T = unknown>(
  url: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* not json */
    }
    const err = new ApiError(res.status, body);
    throw err;
  }
  // Some endpoints (logout) return empty body
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    super(`api error ${status}`);
    this.status = status;
    this.body = body;
  }
}

// ---------------- auth ----------------

export async function adminLogin(password: string): Promise<void> {
  if (!isApiMode()) return; // No-op in static mode
  await apiFetch("/api/admin/login", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export async function adminLogout(): Promise<void> {
  if (!isApiMode()) return;
  await apiFetch("/api/admin/logout", { method: "POST" });
}

/** Returns {role} when the cookie is valid, null when unauthenticated.
 *  In static mode always returns a synthetic role so the admin UI renders
 *  without auth gate (current behaviour). */
export async function adminWhoami(): Promise<{ role: string } | null> {
  if (!isApiMode()) return { role: "static" };
  try {
    return await apiFetch<{ role: string }>("/api/admin/me");
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

// ---------------- packs ----------------

export async function putPack(pack: Pack): Promise<void> {
  if (isApiMode()) {
    await apiFetch("/api/admin/packs", {
      method: "POST",
      body: JSON.stringify(pack),
    });
  }
  // Always mirror to IDB (cache or sole store, depending on mode).
  await idb.putPack(pack);
}

export async function deletePack(id: string): Promise<void> {
  if (isApiMode()) {
    await apiFetch(`/api/admin/packs/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }
  // Cascade is handled server-side via ON DELETE CASCADE. Client mirrors:
  // delete the pack from IDB. Cards remain orphaned in IDB until ensureContent
  // re-fetches, which is fine for the brief admin-view race window.
  const db = await (await import("../db/schema")).getDB();
  await db.delete("packs", id);
}

// ---------------- cards ----------------

export async function putCard(card: Card): Promise<void> {
  if (isApiMode()) {
    await apiFetch("/api/admin/cards", {
      method: "POST",
      body: JSON.stringify(card),
    });
  }
  await idb.putCard(card);
}

export async function deleteCard(id: string): Promise<void> {
  if (isApiMode()) {
    await apiFetch(`/api/admin/cards/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  }
  await idb.deleteCard(id);
}
